import { AdminPermissionsConstants, ApplicationPermissionsConstants } from '@fluentsms/types-fluent';
import { useMemo } from 'react';
import { useAuth } from '../auth';

export const useUserPermissions = () => {
  const { isAuthorized, hasAuthorizedAnywhere, hasAdminAuthorizations } = useAuth();

  return useMemo(() => {
    const canViewWorkflowInsights = hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewWorkflowInsights);
    const canViewOrdersInsights = hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewOrdersInsights);
    const canViewAccountingInsights = hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewAccountingInsights);
    const canView1099Insights = hasAuthorizedAnywhere(ApplicationPermissionsConstants.view1099Insights);
    const canViewAdminInsights = hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewAdminInsights);
    const canViewSalesMarketingInsights = hasAuthorizedAnywhere(
      ApplicationPermissionsConstants.viewSalesMarketingInsights,
    );
    const canAdministerReports = hasAuthorizedAnywhere(ApplicationPermissionsConstants.adminReports);
    const canViewTitleInsights = hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewTitleInsights);
    const canViewAllReports =
      canViewWorkflowInsights ||
      canViewOrdersInsights ||
      canViewAccountingInsights ||
      canView1099Insights ||
      canViewAdminInsights ||
      canViewSalesMarketingInsights ||
      canAdministerReports;
    return {
      hasAdminAuthorizations,

      // Admin permissions — tenant-level, no companyId
      canAccessHierarchy: isAuthorized(AdminPermissionsConstants.accessHierarchy),
      canManageHierarchy: isAuthorized(AdminPermissionsConstants.manageHierarchy),
      canManageTaxIDNumber: isAuthorized(AdminPermissionsConstants.manageTaxIDNumber),
      canManageWireInstructions: isAuthorized(AdminPermissionsConstants.manageWireInstructions),
      canManageWiringAccountNumbers: isAuthorized(AdminPermissionsConstants.manageWiringAccountNumbers),
      canAccessSecurityGroups: isAuthorized(AdminPermissionsConstants.accessSecurityGroups),
      canManageSecurityGroups: isAuthorized(AdminPermissionsConstants.manageSecurityGroups),
      canAccessUsers: isAuthorized(AdminPermissionsConstants.accessUsers),
      canManageUsers: isAuthorized(AdminPermissionsConstants.manageUsers),
      canAccessOrder: isAuthorized(AdminPermissionsConstants.accessOrder),
      canManageOrder: isAuthorized(AdminPermissionsConstants.manageOrder),
      canAccessTitle: isAuthorized(AdminPermissionsConstants.accessTitle),
      canManageTitle: isAuthorized(AdminPermissionsConstants.manageTitle),
      canAccessSettlement: isAuthorized(AdminPermissionsConstants.accessSettlement),
      canManageSettlement: isAuthorized(AdminPermissionsConstants.manageSettlement),
      canAccessFunding: isAuthorized(AdminPermissionsConstants.accessFunding),
      canManageFunding: isAuthorized(AdminPermissionsConstants.manageFunding),
      canAccessInvoices: isAuthorized(AdminPermissionsConstants.accessInvoices),
      canManageInvoices: isAuthorized(AdminPermissionsConstants.manageInvoices),
      canAccessWorkflow: isAuthorized(AdminPermissionsConstants.accessWorkflow),
      canManageWorkflow: isAuthorized(AdminPermissionsConstants.manageWorkflow),
      canAccessDocuments: isAuthorized(AdminPermissionsConstants.accessDocuments),
      canManageDocuments: isAuthorized(AdminPermissionsConstants.manageDocuments),
      canAccessOrganization: isAuthorized(AdminPermissionsConstants.accessOrganization),
      canManageOrganization: isAuthorized(AdminPermissionsConstants.manageOrganization),
      canManageIntegrations: isAuthorized(AdminPermissionsConstants.manageIntegrations),
      canManageDocumentTags: isAuthorized(AdminPermissionsConstants.manageDocumentTags),

      // Application permissions — any company
      canViewOrder: hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewOrder),
      canEditOrder: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editOrder),
      canCreateOrder: hasAuthorizedAnywhere(ApplicationPermissionsConstants.createOrder),
      canViewOrderTaxIDNumber: hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewOrderTaxIDNumber),
      canEditOrderNumber: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editOrderNumber),
      canEditBranchAssignmentPostOrderCreation: hasAuthorizedAnywhere(
        ApplicationPermissionsConstants.editBranchAssignmentPostOrderCreation,
      ),
      canEditTransactionTypePostOrderCreation: hasAuthorizedAnywhere(
        ApplicationPermissionsConstants.editTransactionTypePostOrderCreation,
      ),
      canEditMasterOrders: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editMasterOrders),
      canCreateDisableMasterOrders: hasAuthorizedAnywhere(ApplicationPermissionsConstants.createDisableMasterOrders),
      canViewTitle: hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewTitle),
      canEditTitle: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editTitle),
      canViewPremiumsFeesCalculator: hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewPremiumsFeesCalculator),
      canEditPremiumsFeesCalculator: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editPremiumsFeesCalculator),
      canEditTitlePremiumRateSplits: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editTitlePremiumRateSplits),
      canEditTitlePremiumActualDisclosedAmount: hasAuthorizedAnywhere(
        ApplicationPermissionsConstants.editTitlePremiumActualDisclosedAmount,
      ),
      canEditTitlePremiumDiscount: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editTitlePremiumDiscount),
      canViewSettlement: hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewSettlement),
      canEditSettlement: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editSettlement),
      canViewFunding: hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewFunding),
      canEditFunding: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editFunding),
      canViewInvoices: hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewInvoices),
      canEditInvoices: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editInvoices),
      canViewDocuments: hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewDocuments),
      canEditDocuments: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editDocuments),
      canSendDocumentsViaEmail: hasAuthorizedAnywhere(ApplicationPermissionsConstants.sendDocumentsViaEmail),
      canViewNotes: hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewNotes),
      canCreateNotes: hasAuthorizedAnywhere(ApplicationPermissionsConstants.createNotes),
      canModerateNotes: hasAuthorizedAnywhere(ApplicationPermissionsConstants.notesModerator),
      canPublishNotes: hasAuthorizedAnywhere(ApplicationPermissionsConstants.publishNotes),
      canViewDirectoryManager: hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewDirectoryManager),
      canEditDirectory: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editDirectory),
      canViewDirectoryTaxIDNumber: hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewDirectoryTaxIDNumber),
      canViewDirectoryWiringInstructions: hasAuthorizedAnywhere(
        ApplicationPermissionsConstants.viewDirectoryWiringInstructions,
      ),
      canViewDirectoryWiringAccountNumbers: hasAuthorizedAnywhere(
        ApplicationPermissionsConstants.viewDirectoryWiringAccountNumbers,
      ),
      canViewWorkflow: hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewWorkflow),
      canEditWorkflow: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editWorkflow),
      canViewReports: hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewReports),
      canEditReports: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editReports),
      canEditFundingWireDetails: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editFundingWireDetails),
      canVoidStopPay: hasAuthorizedAnywhere(ApplicationPermissionsConstants.voidStopPay),
      canOverrideTransactionDetails: hasAuthorizedAnywhere(ApplicationPermissionsConstants.overrideTransactionDetails),
      canPrintChecks: hasAuthorizedAnywhere(ApplicationPermissionsConstants.printChecks),
      canViewAccounting: hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewAccounting),
      canEditTransactionBrowser: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editTransactionBrowser),
      canEditWireManagement: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editWireManagement),
      canEditPositivePay: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editPositivePay),
      canEditReconciliation: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editReconciliation),
      canEditPostedTransaction: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editPostedTransaction),
      canEditAdminInsights: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editAdminInsights),
      canEditOrdersInsights: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editOrdersInsights),
      canEditTitleInsights: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editTitleInsights),
      canEditWorkflowInsights: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editWorkflowInsights),
      canEditAccountingInsights: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editAccountingInsights),
      canEdit1099Insights: hasAuthorizedAnywhere(ApplicationPermissionsConstants.edit1099Insights),
      canEditSalesMarketingInsights: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editSalesMarketingInsights),
      canEditRemittance: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editRemittance),
      canViewShipping: hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewShipping),
      canEditShipping: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editShipping),
      canViewContactValidation: hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewContactValidation),
      canEditContactValidation: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editContactValidation),
      canViewIdentityVerification: hasAuthorizedAnywhere(ApplicationPermissionsConstants.viewIdentityVerification),
      canEditIdentityVerification: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editIdentityVerification),
      canEditRecordableDocuments: hasAuthorizedAnywhere(ApplicationPermissionsConstants.editRecordableDocuments),

      // reporting has special logic for their permissions
      canViewTitleInsights,
      canViewWorkflowInsights,
      canViewOrdersInsights,
      canViewAccountingInsights,
      canView1099Insights,
      canViewAdminInsights,
      canViewSalesMarketingInsights,
      canAdministerReports,
      canViewAllReports,
      canAccessAnalytics: canViewTitleInsights || canViewAllReports,
    };
  }, [isAuthorized, hasAuthorizedAnywhere, hasAdminAuthorizations]);
};

export type UserPermissions = ReturnType<typeof useUserPermissions>;
